import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { getErpBranchDetails } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";

// Register fonts if needed
Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Times-Roman",
  },
  container: {
    flex: 1,
  },
  header: {
    alignItems: "center",
    marginBottom: 8,
  },
  companyTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#8c1d1a",
    marginBottom: 4,
  },
  addressText: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 1,
  },
  customerSection: {
    marginBottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  customerLeft: {
    flex: 1,
  },
  customerRight: {
    flex: 1,
    alignItems: "flex-end",
  },
  customerText: {
    fontSize: 10,
    marginBottom: 2,
    fontWeight: "bold",
  },
  table: {
    marginTop: 8,
    border: "1pt solid #000000",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    backgroundColor: "#f0f0f0",
    fontSize: 9,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    fontSize: 9,
    minHeight: 25,
  },
  tableCol1: {
    width: "5%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol2: {
    width: "18%",
    borderRight: "1pt solid #000000",
    padding: 4,
  },
  tableCol3: {
    width: "6%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol4: {
    width: "6%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol5: {
    width: "8%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol6: {
    width: "8%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol7: {
    width: "7%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol8: {
    width: "7%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol9: {
    width: "8%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol10: {
    width: "10%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol11: {
    width: "10%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol12: {
    width: "10%",
    padding: 4,
    textAlign: "center",
  },
  totalsBelowTable: {
    marginTop: 4,
    flexDirection: "row",
    justifyContent: "flex-start",
    fontSize: 9,
    fontWeight: "bold",
    paddingLeft: 4,
  },
  totalsLabel: {
    marginRight: 8,
  },
  totalsValue: {
    marginRight: 20,
  },
  summarySection: {
    marginTop: 8,
    border: "1pt solid #000000",
    padding: 8,
  },
  summaryRow: {
    flexDirection: "row",
    marginBottom: 4,
    fontSize: 9,
  },
  summaryLabel: {
    width: "30%",
    fontWeight: "bold",
  },
  summaryValue: {
    flex: 1,
  },
  summaryAmount: {
    flex: 1,
    textAlign: "right",
    fontWeight: "bold",
  },
  amountInWordsSection: {
    marginBottom: 8,
    fontSize: 9,
    fontWeight: "bold",
  },
  paymentDetailsSection: {
    marginTop: 8,
    border: "1pt solid #000000",
  },
  paymentTableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    minHeight: 20,
  },
  paymentTableLeftCol: {
    width: "50%",
    borderRight: "1pt solid #000000",
    padding: 4,
    fontSize: 9,
  },
  paymentTableRightCol: {
    width: "50%",
    padding: 4,
    fontSize: 9,
  },
  paymentMethodRow: {
    flexDirection: "row",
    marginBottom: 3,
    fontSize: 9,
  },
  paymentMethodLabel: {
    fontWeight: "bold",
    marginRight: 5,
    minWidth: 60,
  },
  paymentMethodValue: {
    flex: 1,
  },
  companyInfo: {
    marginTop: 8,
    fontSize: 8,
    marginBottom: 2,
  },
  jurisdictionText: {
    marginTop: 4,
    fontSize: 8,
    fontStyle: "italic",
  },
  summaryTable: {
    width: "100%",
  },
  summaryTableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    fontSize: 9,
    padding: 2,
    minHeight: 18,
  },
  summaryTableLabel: {
    flex: 1,
    fontWeight: "bold",
  },
  summaryTableValue: {
    width: "35%",
    textAlign: "right",
    fontWeight: "bold",
  },
  footer: {
    marginTop: 8,
    fontSize: 8,
    textAlign: "right",
  },
  certificationText: {
    marginTop: 8,
    fontSize: 8,
    textAlign: "right",
    fontStyle: "italic",
  },
});

// Number to words function
const numberToWords = (num) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if (num === 0) return "Zero";

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100)
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "");
    return (
      ones[Math.floor(n / 100)] +
      " Hundred" +
      (n % 100 !== 0 ? " and " + convertLessThanThousand(n % 100) : "")
    );
  };

  const convert = (n) => {
    if (n === 0) return "";
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000)
      return (
        convertLessThanThousand(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 !== 0 ? " " + convertLessThanThousand(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        convertLessThanThousand(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 !== 0 ? " " + convert(Math.floor(n / 100000)) : "")
      );
    return (
      convertLessThanThousand(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 !== 0 ? " " + convert(Math.floor(n / 10000000)) : "")
    );
  };

  return convert(Math.floor(num));
};

// Old Purchase PDF Component
const OldPurchasePDF = ({ purchaseData }) => {
  const data = purchaseData;

  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyAddress = branchDetails.address || "-";
  const companyMobile = branchDetails.mobile || branchDetails.phone || "-";
  const companyGstin =
    branchDetails.gst_number ||
    branchDetails.gstin ||
    branchDetails.GST_number ||
    "-";
  const companyPan =
    branchDetails.pan_card || branchDetails.pan_no || branchDetails.pan || "-";
  const companyNameUpper = getTenantNameUpper();

  // Calculate totals
  const totalGwt = data.items?.reduce((sum, item) => sum + (parseFloat(item.gwt) || 0), 0) || 0;
  const totalNwt = data.items?.reduce((sum, item) => sum + (parseFloat(item.nwt) || 0), 0) || 0;
  const totalAmount = data.items?.reduce((sum, item) => sum + (parseFloat(item.total_amt) || 0), 0) || 0;
  const paidAmount = data.payments?.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) || 0;
  const roundOff = parseFloat(data.round_off || 0);
  const amountAfterTax = totalAmount + roundOff;
  const advancePayment = parseFloat(data.advancePayment || 0);
  const finalOutstanding = amountAfterTax - paidAmount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.companyTitle}>{companyNameUpper}</Text>
            <Text style={styles.addressText}>{companyAddress}</Text>
            <Text style={styles.addressText}>Mo: {companyMobile}</Text>
          </View>

          {/* Amount in Words - Top Left */}
          <View style={styles.amountInWordsSection}>
            <Text>
              Rupees {numberToWords(parseFloat(amountAfterTax))} Only
            </Text>
          </View>

          {/* Customer Details */}
          <View style={styles.customerSection}>
            <View style={styles.customerLeft}>
              <Text style={styles.customerText}>
                Name: {data.customer?.name || "N/A"}
              </Text>
              <Text style={styles.customerText}>
                Location: {data.customer?.city || "AHMEDABAD"}
              </Text>
            </View>
            <View style={styles.customerRight}>
              <Text style={styles.customerText}>
                Mo.: {data.customer?.mobile || "N/A"}
              </Text>
              <Text style={styles.customerText}>Date: {data.date || ""}</Text>
              <Text style={styles.customerText}>No: {data.invoiceNumber || ""}</Text>
            </View>
          </View>

          {/* Item Table */}
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCol1}>Sr. No.</Text>
              <Text style={styles.tableCol2}>Item Name</Text>
              <Text style={styles.tableCol3}>PCS</Text>
              <Text style={styles.tableCol4}>Ct.</Text>
              <Text style={styles.tableCol5}>Gross Wt.</Text>
              <Text style={styles.tableCol6}>Net. Wt.</Text>
              <Text style={styles.tableCol7}>Touch</Text>
              <Text style={styles.tableCol8}>Waste</Text>
              <Text style={styles.tableCol9}>Fine</Text>
              <Text style={styles.tableCol10}>Labour Rs.</Text>
              <Text style={styles.tableCol11}>Other Cost</Text>
              <Text style={styles.tableCol12}>Amount</Text>
            </View>

            {data.items?.map((item, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={styles.tableCol1}>{index + 1}</Text>
                <Text style={styles.tableCol2}>{item.item_name || ""}</Text>
                <Text style={styles.tableCol3}>{item.pcs || "0"}</Text>
                <Text style={styles.tableCol4}>{item.ct || "0"}</Text>
                <Text style={styles.tableCol5}>
                  {parseFloat(item.gwt || 0).toFixed(3)}
                </Text>
                <Text style={styles.tableCol6}>
                  {parseFloat(item.nwt || 0).toFixed(3)}
                </Text>
                <Text style={styles.tableCol7}>
                  {parseFloat(item.touch || 0).toFixed(2)}
                </Text>
                <Text style={styles.tableCol8}>
                  {parseFloat(item.wastage || 0).toFixed(2)}
                </Text>
                <Text style={styles.tableCol9}>
                  {parseFloat(item.fine_wt || 0).toFixed(3)}
                </Text>
                <Text style={styles.tableCol10}>0</Text>
                <Text style={styles.tableCol11}>0</Text>
                <Text style={styles.tableCol12}>
                  {parseFloat(item.total_amt || 0).toFixed(2)}
                </Text>
              </View>
            ))}
          </View>

          {/* Totals below table */}
          <View style={styles.totalsBelowTable}>
            <Text style={styles.totalsLabel}>Gross Wt.:</Text>
            <Text style={styles.totalsValue}>{totalGwt.toFixed(3)}</Text>
            <Text style={styles.totalsLabel}>Net. Wt.:</Text>
            <Text style={styles.totalsValue}>{totalNwt.toFixed(3)}</Text>
          </View>

          {/* Payment Details Section - Table Format */}
          <View style={styles.paymentDetailsSection}>
            {/* First Row: NOTE and Summary Table Header */}
            <View style={styles.paymentTableRow}>
              <View style={styles.paymentTableLeftCol}>
                <Text style={{ fontWeight: "bold", marginBottom: 4 }}>NOTE:</Text>
                <Text>Rupees {numberToWords(parseFloat(amountAfterTax))} Only</Text>
              </View>
              <View style={styles.paymentTableRightCol}>
                <View style={styles.summaryTable}>
                  <View style={styles.summaryTableRow}>
                    <Text style={styles.summaryTableLabel}>ROUND OFF</Text>
                    <Text style={styles.summaryTableValue}>{roundOff.toFixed(2)}</Text>
                  </View>
                  <View style={styles.summaryTableRow}>
                    <Text style={styles.summaryTableLabel}>AMOUNT AFTER TAX</Text>
                    <Text style={styles.summaryTableValue}>{amountAfterTax.toFixed(2)}</Text>
                  </View>
                  <View style={styles.summaryTableRow}>
                    <Text style={styles.summaryTableLabel}>ADVANCE PAYMENT</Text>
                    <Text style={styles.summaryTableValue}>{advancePayment.toFixed(2)}</Text>
                  </View>
                  <View style={styles.summaryTableRow}>
                    <Text style={styles.summaryTableLabel}>TOTAL PAID</Text>
                    <Text style={styles.summaryTableValue}>{paidAmount.toFixed(2)}</Text>
                  </View>
                  <View style={[styles.summaryTableRow, { borderBottom: 0 }]}>
                    <Text style={styles.summaryTableLabel}>FINAL OUTSTANDING</Text>
                    <Text style={styles.summaryTableValue}>{finalOutstanding.toFixed(2)}</Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Second Row: Payment Methods */}
            <View style={styles.paymentTableRow}>
              <View style={styles.paymentTableLeftCol}>
                {data.payments && data.payments.length > 0 ? (
                  <View>
                    {data.payments.map((payment, index) => (
                      <View key={index} style={styles.paymentMethodRow}>
                        <Text style={styles.paymentMethodLabel}>
                          {payment.bank_name || payment.payment_mode || "Cash"}:
                        </Text>
                        <Text style={styles.paymentMethodValue}>
                          {parseFloat(payment.amount || 0).toFixed(2)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text>Cash: 0.00</Text>
                )}
              </View>
              <View style={styles.paymentTableRightCol}>
                <Text style={{ textAlign: "right", fontSize: 8 }}>
                  Certified that the particulars given above are true and correct.
                </Text>
              </View>
            </View>

            {/* Third Row: Company Information */}
            <View style={styles.paymentTableRow}>
              <View style={styles.paymentTableLeftCol}>
                <View style={styles.companyInfo}>
                  <Text>GSTIN : {companyGstin}</Text>
                  <Text>PAN : {companyPan}</Text>
                  <Text>BIS HALLMARKING LIC. NO : HM/C-7290572420</Text>
                </View>
                <View style={styles.jurisdictionText}>
                  <Text>SUBJECT TO AHMEDABAD JURISDICTION.</Text>
                </View>
              </View>
              <View style={styles.paymentTableRightCol}>
                <View style={{ marginTop: 20, textAlign: "right" }}>
                  <Text style={{ fontSize: 9, marginBottom: 20 }}>For, {companyNameUpper}</Text>
                  <Text style={{ fontSize: 9 }}>AUTHORISED SIGNATURE</Text>
                </View>
              </View>
            </View>

            {/* Fourth Row: Customer Signature */}
            <View style={[styles.paymentTableRow, { borderBottom: 0 }]}>
              <View style={styles.paymentTableLeftCol}>
                <Text style={{ marginTop: 20, fontSize: 9 }}>CUSTOMER SIGNATURE</Text>
              </View>
              <View style={styles.paymentTableRightCol}>
                {/* Empty space for alignment */}
              </View>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text>Page 1 of 1</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default OldPurchasePDF;
